import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PaperAirplaneIcon,
  MicrophoneIcon,
  RectangleStackIcon,
  PuzzlePieceIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { SparklesIcon as SparklesSolid } from '@heroicons/react/24/solid';
import { aiChatService, AIChatMessage, SmartSuggestion } from '../services/aiChatService';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { elevenLabsSTT } from '../utils/elevenLabsSTT';
import toast from 'react-hot-toast';

interface AIChatBoxProps {
  showCreationOptions?: boolean;
  onDeckCreated?: () => void;
  onQuizCreated?: () => void;
  onMatchCreated?: () => void;
}

// Extend Window interface for Speech Recognition
interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: (event: SpeechRecognitionEvent) => void;
  onerror: (event: SpeechRecognitionErrorEvent) => void;
  onend: () => void;
  onstart: () => void;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}

const AIChatBox: React.FC<AIChatBoxProps> = ({
  showCreationOptions = false,
  onDeckCreated,
  onQuizCreated,
  onMatchCreated,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscription } = useSubscription();
  
  // Free tier limits
  const FREE_TIER_LIMITS = {
    maxCardsPerDeck: 100,
    maxQuestionsPerQuiz: 15,
    maxPairsPerMatch: 10,
  };
  
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<AIChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [useElevenLabsSTT, setUseElevenLabsSTT] = useState(false);
  const [quickSuggestions, setQuickSuggestions] = useState<SmartSuggestion[]>([]);
  const [isParsingVoice, setIsParsingVoice] = useState(false);
  const [selectedCreationType, setSelectedCreationType] = useState<'deck' | 'quiz' | 'match' | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  const userName = user?.name?.split(' ')[0] || 'usuário';

  // Helper function to render message content with clickable links and buttons
  const renderMessageContent = (content: string) => {
    // Extract markdown links [text](url)
    const linkMatches = content.match(/\[.*?\]\(.*?\)/g);
    
    if (!linkMatches || linkMatches.length === 0) {
      // No links found, return content as is
      return <span>{content}</span>;
    }

    // Split content by markdown links
    const parts = content.split(/(\[.*?\]\(.*?\))/g);
    const elements: JSX.Element[] = [];
    let hasNavigationLinks = false;
    const navigationLinks: { text: string; url: string }[] = [];
    
    parts.forEach((part, index) => {
      const linkMatch = part.match(/\[(.*?)\]\((.*?)\)/);
      if (linkMatch) {
        const [, text, url] = linkMatch;
        // Check if this is a navigation link (internal link starting with /)
        if (url.startsWith('/')) {
          hasNavigationLinks = true;
          navigationLinks.push({ text, url });
        } else {
          // External link - render as regular link
          elements.push(
            <a
              key={index}
              href={url}
              className="underline font-semibold hover:opacity-80 transition-opacity"
              target="_blank"
              rel="noopener noreferrer"
            >
              {text}
            </a>
          );
        }
      } else if (part.trim()) {
        elements.push(<span key={index}>{part}</span>);
      }
    });

    // If we have navigation links, render the text without links and add buttons below
    if (hasNavigationLinks) {
      // Extract key parts: greeting and emoji
      const greeting = content.match(/^([^!]+!)/)?.[1] || 'Ótima escolha!';
      const emoji = content.match(/[🎯🚀🎮🧩📚✨💎🎉👉🔥⚡]/)?.[0] || '';
      
      // Create simple message
      const cleanText = `${greeting} Escolha uma opção abaixo ${emoji}`.trim();
      
      // Get icon based on URL
      const getIconForUrl = (url: string) => {
        if (url.includes('/decks') || url.includes('/flashcard')) return '📚';
        if (url.includes('/quiz')) return '🧩';
        if (url.includes('/match')) return '✨';
        if (url.includes('/pricing')) return '💎';
        return '👉';
      };
      
      return (
        <div className="space-y-2">
          <span>{cleanText}</span>
            <div className="flex flex-wrap gap-2 pt-1">
            {navigationLinks.map((link, idx) => (
              <button
                key={idx}
                onClick={() => navigate(link.url)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
              >
                <span>{getIconForUrl(link.url)}</span>
                <span>{link.text}</span>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      );
    }

    // No navigation links, return regular elements
    return <>{elements}</>;
  };

  // Load initial suggestions
  useEffect(() => {
    const loadSuggestions = async () => {
      const suggestions = await aiChatService.getInitialSuggestions();
      setQuickSuggestions(suggestions);
    };
    loadSuggestions();
  }, []);

  // Initialize speech recognition
  useEffect(() => {
    // Check if ElevenLabs STT is available (has API key)
    if (elevenLabsSTT.isAvailable()) {
      setSpeechSupported(true);
      setUseElevenLabsSTT(true);
      console.log('🎤 Using Speech-to-Text');
    } else {
      // Fallback to browser native speech recognition
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      
      if (SpeechRecognition) {
        setSpeechSupported(true);
        setUseElevenLabsSTT(false);
        console.log('🎤 Using browser native Speech Recognition');
        
        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = true;
        recognition.lang = 'pt-BR'; // Portuguese (Brazil)
        
        recognition.onstart = () => {
          setIsListening(true);
        };
        
        recognition.onresult = (event: SpeechRecognitionEvent) => {
          let finalTranscript = '';
          let interimTranscript = '';
          
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript;
            } else {
              interimTranscript += transcript;
            }
          }
          
          // Update message with the transcript
          if (finalTranscript) {
            setMessage((prev) => prev + finalTranscript);
          } else if (interimTranscript) {
            // Show interim results (optional - for real-time feedback)
            setMessage((prev) => {
              // Remove previous interim result and add new one
              const baseMessage = prev.replace(/\[\.\.\.\]$/, '').trim();
              return baseMessage ? `${baseMessage} ${interimTranscript}` : interimTranscript;
            });
          }
        };
        
        recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
          
          if (event.error === 'not-allowed') {
            toast.error('Permissão de microfone negada. Por favor, permita o acesso ao microfone.');
          } else if (event.error === 'no-speech') {
            toast.error('Nenhuma fala detectada. Tente novamente.');
          } else if (event.error !== 'aborted') {
            toast.error('Erro no reconhecimento de voz. Tente novamente.');
          }
        };
        
        recognition.onend = () => {
          setIsListening(false);
        };
        
        recognitionRef.current = recognition;
      } else {
        setSpeechSupported(false);
      }
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
      // Cancel any ongoing ElevenLabs recording
      if (elevenLabsSTT.isRecording()) {
        elevenLabsSTT.cancelRecording();
      }
    };
  }, []);

  const toggleListening = async () => {
    // Use ElevenLabs STT if available
    if (useElevenLabsSTT) {
      if (isListening) {
        // Stop recording and transcribe
        setIsTranscribing(true);
        setIsParsingVoice(true);
        try {
          const result = await elevenLabsSTT.stopRecordingAndTranscribe({ language: 'pt' });
          if (result.text.trim()) {
            // Show the transcription briefly
            toast.success(`🎤 "${result.text}"`, { duration: 2000, icon: '✨' });
            
            // Automatically send the transcribed message with smart parsing
            setIsListening(false);
            setIsTranscribing(false);
            
            // Use handleSendMessage with voice flag for smart parsing
            await handleSendMessage(result.text, true);
          } else {
            toast.error('Nenhuma fala detectada. Tente novamente.');
          }
        } catch (error: any) {
          console.error('STT error:', error);
          if (error.message.includes('too short')) {
            toast.error('Gravação muito curta. Fale por mais tempo.');
          } else if (error.message.includes('permission')) {
            toast.error('Permissão de microfone negada.');
          } else {
            toast.error('Erro na transcrição. Tente novamente.');
          }
        } finally {
          setIsListening(false);
          setIsTranscribing(false);
          setIsParsingVoice(false);
        }
      } else {
        // Start recording
        try {
          await elevenLabsSTT.startRecording();
          setIsListening(true);
          toast.success('🎤 Gravando... Fale agora!', { duration: 2000 });
        } catch (error: any) {
          console.error('Error starting recording:', error);
          if (error.message.includes('permission')) {
            toast.error('Permissão de microfone negada. Por favor, permita o acesso.');
          } else {
            toast.error('Erro ao iniciar gravação.');
          }
        }
      }
      return;
    }
    
    // Fallback to browser native speech recognition
    if (!recognitionRef.current) {
      toast.error('Reconhecimento de voz não suportado neste navegador.');
      return;
    }
    
    if (isListening) {
      recognitionRef.current.stop();
    } else {
      try {
        recognitionRef.current.start();
        toast.success('Ouvindo... Fale agora!', { duration: 2000, icon: '🎤' });
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        toast.error('Erro ao iniciar reconhecimento de voz.');
      }
    }
  };

  // Get conversation context for AI parsing
  const getConversationContext = () => {
    return messages.slice(-6).map(m => ({
      role: m.role,
      content: m.content
    }));
  };

  // Check if user is on free tier and count exceeds limit
  const checkFreeTierLimit = (
    intent: string, 
    count: number | undefined
  ): { exceedsLimit: boolean; maxAllowed: number; itemType: string } | null => {
    if (!count || !subscription || subscription.subscriptionTier !== 'free') {
      return null;
    }

    if (intent === 'create_deck' && count > FREE_TIER_LIMITS.maxCardsPerDeck) {
      return { exceedsLimit: true, maxAllowed: FREE_TIER_LIMITS.maxCardsPerDeck, itemType: 'cartões' };
    }
    if (intent === 'create_quiz' && count > FREE_TIER_LIMITS.maxQuestionsPerQuiz) {
      return { exceedsLimit: true, maxAllowed: FREE_TIER_LIMITS.maxQuestionsPerQuiz, itemType: 'questões' };
    }
    if (intent === 'create_match' && count > FREE_TIER_LIMITS.maxPairsPerMatch) {
      return { exceedsLimit: true, maxAllowed: FREE_TIER_LIMITS.maxPairsPerMatch, itemType: 'pares' };
    }

    return null;
  };

  // Generate upgrade required message
  const generateUpgradeMessage = (maxAllowed: number, itemType: string): AIChatMessage => {
    return {
      id: `msg_${Date.now()}`,
      role: 'assistant',
      content: `⚠️ No plano gratuito, você pode criar até **${maxAllowed} ${itemType}** por vez.\n\n⏱️ Desbloqueie acesso completo por apenas R$ 2,90 por 24 horas! [Ver opções de planos](/pricing)\n\nOu faça upgrade para o plano **Premium** para criar conteúdo sem limites! 🚀`,
      timestamp: new Date(),
      suggestions: [
        {
          id: 'go_pricing',
          type: 'quick_action',
          label: '💎 Ver planos Premium',
          action: { type: 'navigate', payload: { navigateTo: '/pricing' } }
        },
        {
          id: 'use_free_limit',
          type: 'quick_action',
          label: `✅ Criar ${maxAllowed} ${itemType}`,
          action: { type: 'send_message', payload: { message: `${maxAllowed}` } }
        }
      ]
    };
  };

  const selectCreationType = (type: 'deck' | 'quiz' | 'match') => {
    setSelectedCreationType(type);
    const instructions = {
      deck: 'Qual tema você quer estudar? Vou criar 20 flashcards.',
      quiz: 'Qual tema você quer estudar? Vou criar um quiz com 15 questões de múltipla escolha e verdadeiro ou falso.',
      match: 'Qual tema você quer estudar? Vou criar um jogo com 12 pares.',
    };
    setMessages([{
      id: `msg_${Date.now()}`,
      role: 'assistant',
      content: instructions[type],
      timestamp: new Date(),
    }]);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const createSelectedResource = async (topic: string) => {
    if (!selectedCreationType || isCreating) return;

    setIsCreating(true);
    const loadingToast = toast.loading('Criando com IA...');

    try {
      let resultMessage: string;

      if (selectedCreationType === 'deck') {
        const result = await aiChatService.createAIDeck({
          topic,
          cardCount: 20,
          difficulty: 'intermediate',
          language: 'portuguese',
        });
        if (!result.success || !result.deckId) {
          throw new Error('A criação dos flashcards não retornou um link para o recurso.');
        }
        resultMessage = `✅ Seu conjunto de 20 flashcards sobre "${topic}" foi criado! [Abrir flashcards](/decks/management/${result.deckId})`;
        onDeckCreated?.();
      } else if (selectedCreationType === 'quiz') {
        const result = await aiChatService.createAIQuiz({
          topic,
          questionCount: 15,
          difficulty: 'intermediate',
          language: 'portuguese',
          questionTypes: { multipleChoice: 8, trueFalse: 7 },
        });
        if (!result.success || !result.quizId) {
          throw new Error('A criação do quiz não retornou um link para o recurso.');
        }
        resultMessage = `✅ Seu quiz de 15 questões sobre "${topic}" foi criado com questões de múltipla escolha e verdadeiro ou falso! [Abrir quiz](/quizzes/management/${result.quizId})`;
        onQuizCreated?.();
      } else {
        const result = await aiChatService.createAIMatch({
          topic,
          pairsCount: 12,
          difficulty: 'medium',
          language: 'portuguese',
        });
        if (!result.success || !result.matchGameId) {
          throw new Error('A criação do jogo não retornou um link para o recurso.');
        }
        resultMessage = `✅ Seu jogo de combinação com 12 pares sobre "${topic}" foi criado! [Abrir jogo](/matches/management/${result.matchGameId})`;
        onMatchCreated?.();
      }

      toast.success('Conteúdo criado com sucesso!', { id: loadingToast });
      setMessages((previous) => [
        ...previous,
        {
          id: `msg_${Date.now()}`,
          role: 'assistant',
          content: resultMessage,
          timestamp: new Date(),
        },
      ]);
    } catch (error: any) {
      console.error('Error creating resource from chat:', error);
      const errorText = error.response?.data?.message || error.message || 'Não foi possível criar o conteúdo. Tente novamente.';
      toast.error(errorText, { id: loadingToast });
      setMessages((previous) => [
        ...previous,
        {
          id: `msg_${Date.now()}`,
          role: 'assistant',
          content: `❌ ${errorText}`,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsCreating(false);
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (overrideMessage?: string, isFromVoice: boolean = false) => {
    const textToSend = overrideMessage || message.trim();
    if (!textToSend || isLoading || isCreating) return;

    const userMessage: AIChatMessage = {
      id: `msg_${Date.now()}`,
      role: 'user',
      content: textToSend,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setMessage('');
    setIsLoading(true);

    if (selectedCreationType) {
      await createSelectedResource(textToSend);
      return;
    }

    // Use smart parsing for voice input, complex messages, or when we have conversation context that might be relevant
    const conversationContext = getConversationContext();
    const hasRelevantContext = conversationContext.length > 0;
    const isSimpleKeyword = /^(quiz|deck|match|flashcard|flashcards|cartões|baralho|questionário|prova|jogo|pareamento)$/i.test(textToSend.trim());
    const shouldUseSmartParsing = isFromVoice || textToSend.length > 50 || (!/criar|create|ajuda|help/i.test(textToSend) && !isSimpleKeyword);

    try {
      let assistantMessage: AIChatMessage;
      let parsedIntent;

      if (shouldUseSmartParsing && !isSimpleKeyword) {
        // Use AI-powered smart parsing for better understanding
        parsedIntent = await aiChatService.smartParseIntent(
          textToSend,
          conversationContext
        );
      } else {
        // Use local parsing for simple, clear commands - now with context support
        parsedIntent = aiChatService.localParseIntent(textToSend, conversationContext);
      }

      // Check if user is on free tier and requested count exceeds limit
      const limitCheck = checkFreeTierLimit(
        parsedIntent.intent,
        parsedIntent.extractedParams.count
      );

      if (limitCheck?.exceedsLimit) {
        // User is on free tier and wants more than allowed
        assistantMessage = generateUpgradeMessage(limitCheck.maxAllowed, limitCheck.itemType);
        setQuickSuggestions(assistantMessage.suggestions || []);
      } else {
        // Normal flow - generate response from intent
        assistantMessage = aiChatService.generateResponseFromIntent(parsedIntent);
        
        // Update quick suggestions from the response
        if (parsedIntent.suggestions && parsedIntent.suggestions.length > 0) {
          setQuickSuggestions(parsedIntent.suggestions);
        }
      }

      // Small delay for natural feel
      await new Promise(resolve => setTimeout(resolve, 300));

      setMessages((prev) => [...prev, assistantMessage]);
      setIsLoading(false);

      // Note: Navigation links are provided in the message content as clickable markdown links
      // The user can click them to navigate manually
    } catch (error) {
      console.error('Error processing message:', error);
      setIsLoading(false);
      
      // Fallback response on error
      const fallbackMessage: AIChatMessage = {
        id: `msg_${Date.now()}`,
        role: 'assistant',
        content: '😅 Desculpe, tive um problema ao processar sua mensagem. Pode tentar novamente?',
        timestamp: new Date(),
        suggestions: aiChatService.getDefaultSuggestions()
      };
      setMessages((prev) => [...prev, fallbackMessage]);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle clicks on smart suggestion chips
  const handleSuggestionClick = async (suggestion: SmartSuggestion) => {
    if (!suggestion.action) return;

    switch (suggestion.action.type) {
      case 'send_message':
        if (suggestion.action.payload?.message) {
          await handleSendMessage(suggestion.action.payload.message);
        }
        break;
      case 'create_deck':
        await handleCreateAction({
          type: 'create_deck',
          payload: suggestion.action.payload
        });
        break;
      case 'create_quiz':
        await handleCreateAction({
          type: 'create_quiz',
          payload: suggestion.action.payload
        });
        break;
      case 'create_match':
        await handleCreateAction({
          type: 'create_match',
          payload: suggestion.action.payload
        });
        break;
      case 'navigate':
        if (suggestion.action.payload?.navigateTo) {
          navigate(suggestion.action.payload.navigateTo);
        }
        break;
    }
  };

  const handleCreateAction = async (action: AIChatMessage['action']) => {
    if (!action || isCreating) return;

    setIsCreating(true);
    const loadingToast = toast.loading('Criando com IA...');

    try {
      switch (action.type) {
        case 'create_deck':
          await aiChatService.createAIDeck({
            topic: action.payload?.topic || '',
            cardCount: action.payload?.cardCount || 10,
            difficulty: (action.payload?.difficulty as 'beginner' | 'intermediate' | 'advanced') || 'intermediate',
            language: (action.payload?.language as 'portuguese' | 'english' | 'spanish') || 'portuguese',
          });
          toast.success('Deck está sendo criado! Você receberá uma notificação quando estiver pronto.', { id: loadingToast });
          onDeckCreated?.();
          break;

        case 'create_quiz':
          // Calculate a good mix of question types based on total question count
          const totalQuestions = action.payload?.questionCount || 10;
          const questionTypes = action.payload?.questionTypes || {
            multipleChoice: Math.ceil(totalQuestions * 0.4),  // 40% multiple choice
            trueFalse: Math.ceil(totalQuestions * 0.2),       // 20% true/false
            fillBlank: Math.ceil(totalQuestions * 0.2),       // 20% fill blank
            essay: Math.floor(totalQuestions * 0.2),          // 20% essay
          };
          
          await aiChatService.createAIQuiz({
            topic: action.payload?.topic || '',
            questionCount: totalQuestions,
            difficulty: (action.payload?.difficulty as 'beginner' | 'intermediate' | 'advanced') || 'intermediate',
            language: (action.payload?.language as 'portuguese' | 'english' | 'spanish') || 'portuguese',
            questionTypes,
          });
          toast.success('Quiz está sendo criado com diferentes tipos de questões! Você receberá uma notificação quando estiver pronto.', { id: loadingToast });
          onQuizCreated?.();
          break;

        case 'create_match':
          await aiChatService.createAIMatch({
            topic: action.payload?.topic || '',
            pairsCount: action.payload?.pairsCount || 8,
            difficulty: (action.payload?.difficulty as 'easy' | 'medium' | 'hard') || 'medium',
            language: (action.payload?.language as 'portuguese' | 'english' | 'spanish') || 'portuguese',
          });
          toast.success('Match está sendo criado! Você receberá uma notificação quando estiver pronto.', { id: loadingToast });
          onMatchCreated?.();
          break;
      }

      // Add success message
      const successMessage: AIChatMessage = {
        id: `msg_${Date.now()}`,
        role: 'assistant',
        content: '✅ Pronto! A criação foi iniciada. Você pode continuar usando o Upmindy enquanto processamos sua solicitação. O que mais posso fazer por você?',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, successMessage]);
    } catch (error: any) {
      console.error('Error creating with AI:', error);
      
      // Handle deck limit error with upgrade prompt
      if (error.response?.data?.code === 'DECK_LIMIT_REACHED') {
        toast.error('Você atingiu o limite de 3 decks no plano gratuito. Faça upgrade para Premium!', { id: loadingToast });
        const errorMessage: AIChatMessage = {
          id: `msg_${Date.now()}`,
          role: 'assistant',
          content: '❌ Você atingiu o limite de 3 decks no plano gratuito.\n\n⏱️ Desbloqueie acesso completo por apenas R$ 2,90 por 24 horas! [Ver opções de planos](/pricing)\n\nOu faça upgrade para o plano Premium para criar decks ilimitados! 🚀',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
      // Handle quiz limit error with upgrade prompt
      else if (error.response?.data?.code === 'QUIZ_LIMIT_REACHED') {
        toast.error('Você atingiu o limite de 3 quizzes no plano gratuito. Faça upgrade para Premium!', { id: loadingToast });
        const errorMessage: AIChatMessage = {
          id: `msg_${Date.now()}`,
          role: 'assistant',
          content: '❌ Ops! Você já criou 3 quizzes, que é o limite do plano gratuito.\n\n⏱️ Desbloqueie acesso completo por apenas R$ 2,90 por 24 horas! [Ver opções de planos](/pricing)\n\nOu faça upgrade para o plano Premium para criar quizzes ilimitados! 🚀',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
      // Handle AI card limit error
      else if (error.response?.data?.code === 'AI_CARD_LIMIT_REACHED') {
        toast.error('Limite de cards atingido para usuários gratuitos', { id: loadingToast });
        const errorMessage: AIChatMessage = {
          id: `msg_${Date.now()}`,
          role: 'assistant',
          content: '❌ Usuários gratuitos podem criar decks com IA de até 20 cards.\n\n⏱️ Desbloqueie acesso completo por apenas R$ 2,90 por 24 horas! [Ver opções de planos](/pricing)\n\nOu faça upgrade para o plano Premium para cards ilimitados! 🚀',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
      // Handle quiz question limit error
      else if (error.response?.data?.code === 'QUIZ_QUESTION_LIMIT_REACHED') {
        toast.error(error.response?.data?.message || 'Limite de questões atingido. Faça upgrade para Premium!', { id: loadingToast });
        const errorMessage: AIChatMessage = {
          id: `msg_${Date.now()}`,
          role: 'assistant',
          content: `❌ ${error.response?.data?.message || 'Você atingiu o limite de questões para criação de quizzes com IA. Faça upgrade para Premium!'} 🚀`,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
      // Handle other errors
      else {
        toast.error(error.response?.data?.message || 'Erro ao criar. Tente novamente.', { id: loadingToast });
        const errorMessage: AIChatMessage = {
          id: `msg_${Date.now()}`,
          role: 'assistant',
          content: '❌ Desculpe, ocorreu um erro ao processar sua solicitação. Por favor, tente novamente ou reformule seu pedido.',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="mx-auto flex h-full min-h-0 w-full max-w-3xl flex-col px-0">
      {messages.length === 0 && (
        showCreationOptions ? (
          <div className="mb-5 flex items-start gap-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600">
              <SparklesSolid className="h-5 w-5 text-white" aria-hidden="true" />
            </div>
            <div className="rounded-2xl rounded-tl-md bg-white px-4 py-3 text-sm text-gray-800 shadow-sm dark:bg-gray-800 dark:text-gray-100">
              Olá, {userName}. O que vamos criar hoje?
            </div>
          </div>
        ) : (
          <div className="mb-6 text-center sm:mb-8">
            <div className="mb-4 inline-flex items-center justify-center sm:mb-5">
              <div className="relative">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg sm:h-20 sm:w-20">
                  <SparklesSolid className="h-8 w-8 text-white sm:h-10 sm:w-10" />
                </div>
                <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-3 border-white bg-green-500 dark:border-gray-900 sm:-bottom-1.5 sm:-right-1.5 sm:h-6 sm:w-6" />
              </div>
            </div>
            <h2 className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text px-2 text-xl font-bold leading-tight text-transparent sm:text-3xl">
              Olá, {userName}. O que você quer aprender hoje?
            </h2>
          </div>
        )
      )}

      {showCreationOptions && messages.length === 0 && (
        <div className="mx-auto mb-6 w-full max-w-xl">
          <p className="mb-3 text-center text-xs text-gray-500 dark:text-gray-400">Escolha uma opção:</p>
          <div className="flex flex-col items-center gap-3">
            {([
              {
                type: 'deck' as const,
                title: 'Flashcards para memorização',
                detail: '20 cartões',
                icon: RectangleStackIcon,
              },
              {
                type: 'quiz' as const,
                title: 'Quiz para testar conhecimentos',
                detail: '15 questões',
                icon: PuzzlePieceIcon,
              },
              {
                type: 'match' as const,
                title: 'Jogo de combinação para memorização',
                detail: '12 pares',
                icon: SparklesIcon,
              },
            ]).map(({ type, title, detail, icon: Icon }) => (
              <button
                key={type}
                type="button"
                onClick={() => selectCreationType(type)}
                aria-label={`${title}, ${detail}`}
                className="group w-fit max-w-full rounded-full bg-gradient-to-r from-blue-500 via-violet-500 to-fuchsia-500 p-[1.5px] text-white shadow-[0_0_20px_rgba(139,92,246,0.12)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_28px_rgba(139,92,246,0.3)] focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
              >
                <span className="flex min-h-11 items-center justify-center gap-2.5 rounded-full bg-gray-950 px-5 py-2.5 text-center transition-colors group-hover:bg-gray-900">
                  <Icon className="h-4 w-4 flex-shrink-0 text-violet-300" aria-hidden="true" />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold leading-snug">{title}</span>
                    <span className="mt-0.5 block text-xs text-gray-400">{detail}</span>
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Chat Messages */}
      {messages.length > 0 && (
        <div className="mb-3 flex min-h-0 flex-1 flex-col justify-end space-y-4 overflow-y-auto sm:mb-4">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 shadow-sm sm:max-w-[80%] ${
                  msg.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'rounded-tl-md bg-white text-gray-900 dark:bg-gray-800 dark:text-white'
                }`}
              >
                <p className="whitespace-pre-wrap text-sm leading-relaxed sm:text-base">
                  {renderMessageContent(msg.content)}
                </p>
                
                {/* Action Button */}
                {msg.action && msg.action.type !== 'navigate' && msg.action.type !== 'info' && (
                  <button
                    onClick={() => handleCreateAction(msg.action)}
                    disabled={isCreating}
                    className={`mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${
                      isCreating
                        ? 'cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-900/60'
                    }`}
                  >
                    {isCreating ? (
                      <span className="flex items-center justify-center">
                        <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Criando...
                      </span>
                    ) : (
                      <>
                        ✨ Criar {msg.action.type === 'create_deck' ? 'Deck' : msg.action.type === 'create_quiz' ? 'Quiz' : 'Match'}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
          
          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl px-6 py-4 shadow-sm">
                <div className="flex space-x-3">
                  <div className="w-3 h-3 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-3 h-3 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-3 h-3 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Voice Parsing Indicator */}
      {isParsingVoice && (
        <div className="mb-5 flex items-center justify-center gap-3 text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 py-3 px-5 rounded-xl">
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-base font-medium">Entendendo sua mensagem...</span>
        </div>
      )}

      {/* Input Box */}
      {(!showCreationOptions || selectedCreationType || messages.length > 0) && (
      <div className="mt-auto rounded-2xl border border-gray-200 bg-white p-2.5 shadow-sm transition-colors focus-within:border-blue-400 dark:border-gray-700 dark:bg-gray-800 dark:focus-within:border-blue-600 sm:p-4">
        <div className="flex items-center gap-1.5 sm:gap-3">
          <textarea
            ref={inputRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Mensagem..."
            className="min-h-[40px] max-h-32 flex-1 resize-none border-none bg-transparent px-1 py-2 text-sm leading-relaxed text-gray-900 outline-none placeholder-gray-400 dark:text-white dark:placeholder-gray-500 sm:text-base sm:max-h-40"
            rows={1}
          />
          <div className="flex items-center gap-2 sm:gap-3 pb-1 flex-shrink-0">
            {speechSupported && (
              <button
                onClick={toggleListening}
                disabled={isLoading || isTranscribing}
                aria-label={isTranscribing ? 'Transcrevendo' : isListening ? 'Parar gravação' : 'Gravar mensagem'}
                className={`rounded-xl p-2 transition-all active:scale-95 sm:p-3 ${
                  isTranscribing
                    ? 'bg-purple-500 text-white animate-pulse shadow-lg cursor-wait'
                    : isListening
                    ? 'bg-red-500 text-white animate-pulse shadow-lg'
                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                title={isTranscribing ? 'Transcrevendo...' : isListening ? 'Parar gravação' : 'Falar'}
              >
                {isTranscribing ? (
                  <div className="relative">
                    <svg className="w-6 h-6 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : isListening ? (
                  <div className="relative">
                    <MicrophoneIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-white rounded-full animate-ping" />
                  </div>
                ) : (
                  <MicrophoneIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                )}
              </button>
            )}
            <button
              onClick={() => handleSendMessage()}
              disabled={!message.trim() || isLoading || isCreating}
              aria-label="Enviar mensagem"
              className={`rounded-full p-2.5 transition-all active:scale-95 sm:p-3 ${
                message.trim() && !isLoading
                  ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
              }`}
            >
              <PaperAirplaneIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>
        </div>
      </div>
      )}

    </div>
  );
};

export default AIChatBox;
